import { useEffect } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';

const LAST_CHECK_KEY = 'valnexo_uatu_daily_check';

/**
 * Composant "poussée matinale" — au 1er login du jour, appelle le briefing IA,
 * et si des alertes critiques sont détectées :
 *  - montre un toast persistant "Uatu a repéré N dossiers critiques"
 *  - déclenche une notification browser (si autorisée)
 *
 * Version simplifiée sans service worker/push subscription : léger, sans dépendance,
 * fonctionne dès que l'utilisateur ouvre l'app le matin (ou après > 12h d'absence).
 */
export default function UatuDailyPushWatcher() {
    const { user } = useAuth();

    useEffect(() => {
        if (!user) return;

        const today = new Date().toISOString().slice(0, 10);
        let lastCheck = null;
        try { lastCheck = localStorage.getItem(LAST_CHECK_KEY); } catch { /* noop */ }

        // Ne déclenche qu'une fois par jour
        if (lastCheck === today) return;

        (async () => {
            try {
                const r = await api.get('/dashboard/ai-briefing');
                const alerts = (r.data?.alerts || []).filter((a) => a.level === 'critical');
                if (alerts.length > 0) {
                    const totalCritical = alerts.reduce((sum, a) => sum + (a.count || 0), 0);

                    // Toast persistant
                    toast.warning(
                        `Uatu a repéré ${totalCritical} dossier${totalCritical > 1 ? 's' : ''} critique${totalCritical > 1 ? 's' : ''} ce matin`,
                        {
                            description: alerts[0]?.text || '',
                            duration: 10000,
                            action: {
                                label: 'Voir',
                                onClick: () => { if (alerts[0]?.cta_url) window.location.href = alerts[0].cta_url; },
                            },
                        }
                    );

                    // Notification browser (si autorisée)
                    if (typeof Notification !== 'undefined') {
                        if (Notification.permission === 'granted') {
                            new Notification('Uatu — Priorités du matin', {
                                body: `${totalCritical} dossier${totalCritical > 1 ? 's' : ''} critique${totalCritical > 1 ? 's' : ''} à traiter aujourd'hui.`,
                                icon: '/logo192.png',
                                tag: 'uatu-daily',
                                requireInteraction: false,
                            });
                        } else if (Notification.permission === 'default') {
                            // Demande la permission (silencieusement)
                            Notification.requestPermission().catch(() => { /* noop */ });
                        }
                    }
                }
                try { localStorage.setItem(LAST_CHECK_KEY, today); } catch { /* noop */ }
            } catch { /* silent */ }
        })();
    }, [user]);

    return null;
}
